import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import './Product.css';
import authFetch from '../../utils/authFetch';

const Product = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role must be declared before any early returns
  const [role, setRole] = useState({ isAdmin: false, isPublisher: false, status: null });
  useEffect(() => {
    const loadRole = async () => {
      try {
        const res = await authFetch('/api/store/publisher/me/role');
        const json = await res.json();
        if (json) setRole(json);
      } catch {}
    };
    loadRole();
  }, []);

  // Load product data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/store/products/${slug}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || 'Failed to load product');
        setData(json);
      } catch (e) {
        setError(e.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="ebookstore-product">
      <div className="skeleton-hero">
        <div className="sk-cover" />
        <div className="sk-details">
          <div className="sk-line md" />
          <div className="sk-line sm" />
          <div className="sk-line btn" />
        </div>
      </div>
      <div className="sk-body">
        <div className="sk-line md" />
        <div className="sk-line sm" />
        <div className="sk-line sm" />
      </div>
    </div>
  );
  if (error) return <div className="ebookstore-product"><div className="error">{error}</div></div>;
  if (!data) return null;

  const { product, formats = [], prices = [] } = data;
  const lowest = prices.length ? prices.reduce((a, b) => (a.amount <= b.amount ? a : b)) : null;
  const isFree = !lowest;

  const claimFree = async () => {
    try {
      const res = await authFetch('/api/store/freebies/claim', { method: 'POST', body: { productSlug: product.slug } });
      const json = await res.json();
      if (res.ok && json?.success) {
        alert('Free product claimed! Check your Library.');
      } else {
        alert(json?.message || 'Could not claim this product');
      }
    } catch (e) {
      alert('Claim failed');
    }
  };

  return (
    <div className="ebookstore-product">
      <div className="crumbs"><Link to="/app/eBookstore">← Back to catalog</Link></div>
      <div className="books-header" style={{marginTop: '-6px'}}>
        <div />
        <div className="header-ctas">
          {role.isAdmin && (
            <Link className="btn-header" to="/app/eBookstore/admin/submissions">Admin Submissions</Link>
          )}
          {role.isPublisher ? (
            <Link className="btn-header" to="/app/eBookstore/publisher/dashboard">Publisher Dashboard</Link>
          ) : (
            <Link className="btn-header accent" to="/app/eBookstore/publisher/onboarding">Become a Publisher</Link>
          )}
        </div>
      </div>
      <div className="product-hero glass">
        <div className="cover"><img src={product.cover_url || ''} alt={product.title} /></div>
        <div className="details">
          <h1>{product.title}</h1>
          {product.subtitle && <h2>{product.subtitle}</h2>}
          <div className="meta">
            <span>{product.language || '—'}</span>
            <span>{product.level || '—'}</span>
          </div>
          <div className="price">
            {lowest ? `$${(lowest.amount/100).toFixed(2)} ${lowest.currency}` : 'Coming soon'}
          </div>
          <div className="actions">
            {isFree ? (
              <button className="btn-claim" onClick={claimFree}>Claim Free</button>
            ) : (
              <button disabled className="btn-neon" title="Checkout not ready yet">Add to Cart</button>
            )}
          </div>
        </div>
      </div>
      <div className="product-body glass">
        {product.description && <p className="description">{product.description}</p>}
        <div className="formats">
          <h3>Available formats</h3>
          <ul>
            {formats.map(f => (
              <li key={f.id}>{f.type.toUpperCase()} {f.size_bytes ? `• ${(f.size_bytes/1024/1024).toFixed(2)} MB` : ''}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Product;
